import type {
  ApiContext,
  ApiNotAuthorized,
  InvalidFormField,
  ScanCandidateUnknown,
  ScanNotFound,
  ScanNotReviewable,
  ScanReviewGroup,
  StoreNotFound,
} from "@hobbyshopify/backend-client";
import { type AsyncResult, ok } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import { errorText } from "../primitives/errors";
import type { ReviewEntry } from "../primitives/review-row";
import { shopEntryId } from "../primitives/workspace";
import type { ReviewQueueRepository } from "../repositories/review-queue-repository";
import type { BackendApi } from "./api";

type ConfirmErrors = ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | InvalidFormField;

/** Fetches the queue into the repository. Single flight. */
export async function loadReviewQueue(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  locationId: string | null;
  reviewQueueRepository: ReviewQueueRepository;
}): Promise<void> {
  if (o.reviewQueueRepository.status.kind === "loading") return;
  o.reviewQueueRepository.setLoading();
  const r = await o.authHandler.callApi((ctx) =>
    o.client.scans.reviewQueue(ctx, shopEntryId(o.shopId, o.locationId), undefined, undefined, { enableNoMatch: true }),
  );
  if (!r.ok) {
    o.reviewQueueRepository.setError(errorText(r.errors));
    return;
  }
  const scans = await o.authHandler.callApi((ctx) =>
    o.client.scans.list(ctx, shopEntryId(o.shopId, o.locationId), { enableNoMatch: true }),
  );
  if (!scans.ok) {
    o.reviewQueueRepository.setError("Could not load unrecognised scans.");
    return;
  }
  o.reviewQueueRepository.setGroups(
    r.value,
    scans.value.filter((scan) => scan.kind === "OWNER" && scan.recognitionOutcome === "no_match"),
  );
  if (
    scans.value.some(
      (scan) =>
        scan.kind === "OWNER" &&
        !scan.cardInfo &&
        scan.scanProgress !== "scan.completed" &&
        scan.scanProgress !== "scan.error" &&
        scan.review === "PENDING",
    )
  ) {
    setTimeout(() => o.reviewQueueRepository.markStale(), 2000);
  }
}

/**
 * Confirms a whole group as one card and, unless the row says otherwise, one
 * lot of N, which is why this is confirm-many and not a loop. The queue is
 * marked stale on success; the drafts controller reloads it.
 */
export async function confirmGroup(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  entry: ReviewEntry;
  reviewQueueRepository: ReviewQueueRepository;
}): AsyncResult<void, ConfirmErrors> {
  const r = await confirmOne(o, o.entry);
  if (!r.ok) return r;
  o.reviewQueueRepository.markStale();
  return ok();
}

/**
 * "Confirm all": sequential on purpose. Each confirm is a transaction that may
 * create the same card as the one before it, and the identity key is what
 * serialises them; racing ten of those just trades a queue for a retry loop.
 * Stops at the first refusal, and the queue is marked stale whenever at least
 * one group went through, so the page shows what did.
 */
export async function confirmGroups(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  entries: readonly ReviewEntry[];
  reviewQueueRepository: ReviewQueueRepository;
}): AsyncResult<void, ConfirmErrors> {
  let confirmed = false;
  for (const entry of o.entries) {
    const r = await confirmOne(o, entry);
    if (!r.ok) {
      if (confirmed) o.reviewQueueRepository.markStale();
      return r;
    }
    confirmed = true;
  }
  if (confirmed) o.reviewQueueRepository.markStale();
  return ok();
}

/**
 * Sends a price only when it changes the card's, since each one is a price
 * history row, and never a floor: the backend saves the floor as given, so
 * sending one would overwrite the shop's own on every copy confirmed.
 */
function confirmOne(
  o: { client: BackendApi; authHandler: AuthHandler<ApiContext>; shopId: string },
  { group, priceCents, quantity }: ReviewEntry,
) {
  return o.authHandler.callApi((ctx) =>
    o.client.scans.confirmMany(ctx, o.shopId, group.scanIds, {
      ...(priceCents !== null ? { priceCents } : {}),
      ...(quantity ? { stock: { quantity } } : {}),
    }),
  );
}

/** Prices every scan of the group from the chosen candidate: the group is one card, so its scans move together. */
export async function pickGroupCandidate(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  group: ScanReviewGroup;
  productId: string;
  reviewQueueRepository: ReviewQueueRepository;
}): AsyncResult<void, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | ScanCandidateUnknown> {
  for (const scanId of o.group.scanIds) {
    const r = await o.authHandler.callApi((ctx) => o.client.scans.pick(ctx, o.shopId, scanId, o.productId));
    if (!r.ok) {
      o.reviewQueueRepository.markStale();
      return r;
    }
  }
  o.reviewQueueRepository.markStale();
  return ok();
}

/**
 * Rejects every scan of a group. One refusal fails the group: a half-rejected
 * group would leave the queue showing scans the reviewer believes they dismissed.
 * Each reject is its own authenticated call, so a retry never replays one that went through.
 */
export async function rejectGroup(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  group: ScanReviewGroup;
  reviewQueueRepository: ReviewQueueRepository;
}): AsyncResult<void, ApiNotAuthorized | StoreNotFound | ScanNotFound> {
  const results = await Promise.all(
    o.group.scanIds.map((id) => o.authHandler.callApi((ctx) => o.client.scans.reject(ctx, o.shopId, id))),
  );
  const failed = results.find((r) => !r.ok);
  if (failed) return failed;
  o.reviewQueueRepository.markStale();
  return ok();
}
